/**
 * PC Builder Saves Logic
 * Handles local storage based saving/loading of builds.
 */

(function() {
  'use strict';

  class PCBuilderSaves {
    constructor() {
      this.STORAGE_KEY = 'ndx_saved_builds';
      this.root = document.querySelector('[data-pc-builder-saves]');
      if (!this.root) return;

      this.saveBtn = this.root.querySelector('[data-save-build-btn]');
      this.savesCountEl = this.root.querySelector('[data-saves-count]');
      this.openModalBtn = this.root.querySelector('[data-open-saves-modal]');
      this.closeModalBtn = this.root.querySelector('[data-close-saves-modal]');
      this.overlay = this.root.querySelector('[data-saves-overlay]');
      this.container = this.root.querySelector('[data-saved-builds-container]');
      this.toastEl = this.root.querySelector('[data-saves-toast]');

      // Dialog elements
      this.saveNameDialog = this.root.querySelector('[data-save-name-dialog]');
      this.saveNameInput = this.root.querySelector('[data-save-name-input]');
      this.saveNameConfirmBtn = this.root.querySelector('[data-save-name-confirm]');
      this.saveNameCancelBtn = this.root.querySelector('[data-save-name-cancel]');

      this.init();
    }

    init() {
      this.updateCount();
      this.bindEvents();
    }

    bindEvents() {
      if (this.saveBtn) {
        this.saveBtn.addEventListener('click', () => this.saveCurrentBuild());
      }
      if (this.openModalBtn) {
        this.openModalBtn.addEventListener('click', () => this.openModal());
      }
      if (this.closeModalBtn) {
        this.closeModalBtn.addEventListener('click', () => this.closeModal());
      }
      if (this.overlay) {
        this.overlay.addEventListener('click', (e) => {
          if (e.target === this.overlay) this.closeModal();
        });
      }

      // Dialog events
      if (this.saveNameInput) {
        this.saveNameInput.addEventListener('keydown', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this._confirmSaveName();
          } else if (e.key === 'Escape') {
            this._closeSaveNameDialog();
          }
        });
      }
      if (this.saveNameConfirmBtn) {
        this.saveNameConfirmBtn.addEventListener('click', () => this._confirmSaveName());
      }
      if (this.saveNameCancelBtn) {
        this.saveNameCancelBtn.addEventListener('click', () => this._closeSaveNameDialog());
      }
      if (this.saveNameDialog) {
        this.saveNameDialog.addEventListener('click', (e) => {
          if (e.target === this.saveNameDialog) this._closeSaveNameDialog();
        });
      }
    }

    getSaves() {
      try {
        return JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '[]');
      } catch (e) {
        return [];
      }
    }

    saveCurrentBuild() {
      const builder = window.pcBuilder && window.pcBuilder.instance;
      if (!builder) {
        this.showToast('PC Builder not initialized', 'error');
        return;
      }

      const selections = builder.selections;
      const componentIds = Object.keys(selections);

      if (componentIds.length === 0) {
        this.showToast('Please select at least one component first.', 'error');
        return;
      }

      const defaultName = `My Build ${new Date().toLocaleDateString()}`;
      this._openSaveNameDialog(defaultName, () => {
        this._commitSave(selections, componentIds);
      });
    }

    deleteSave(id) {
      const saves = this.getSaves().filter(s => s.id !== id);
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves));
      this.updateCount();
      this.renderList();
    }

    loadSave(id) {
      const save = this.getSaves().find(s => s.id === id);
      if (!save || !window.pcBuilder || !window.pcBuilder.applyBuild) return;

      window.pcBuilder.applyBuild(save.data);
      this.closeModal();
      this.showToast(`Loaded: ${save.name}`, 'success');
    }

    updateCount() {
      if (this.savesCountEl) {
        this.savesCountEl.textContent = this.getSaves().length;
      }
    }

    openModal() {
      this.renderList();
      this.overlay.classList.add('is-open');
      this.overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
    }

    closeModal() {
      this.overlay.classList.remove('is-open');
      this.overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    renderList() {
      const saves = this.getSaves();
      if (saves.length === 0) {
        this.container.innerHTML = '<div class="empty-state"><p>You haven\'t saved any builds yet.</p></div>';
        return;
      }

      this.container.innerHTML = saves.map(save => `
        <div class="saved-build-item" data-id="${save.id}">
          <div class="saved-build-info">
            <span class="saved-build-name">${this.escape(save.name)}</span>
            <span class="saved-build-date">${new Date(save.date).toLocaleDateString()} &bull; ${save.itemCount} items</span>
          </div>
          <div class="saved-build-actions">
            <button type="button" class="btn btn-primary btn-sm" data-load="${save.id}">Load</button>
            <button type="button" class="btn btn-danger btn-sm" data-delete="${save.id}">Delete</button>
          </div>
        </div>
      `).join('');

      // Bind dynamic actions
      this.container.querySelectorAll('[data-load]').forEach(btn => {
        btn.addEventListener('click', () => this.loadSave(Number(btn.dataset.load)));
      });
      this.container.querySelectorAll('[data-delete]').forEach(btn => {
        btn.addEventListener('click', () => this.deleteSave(Number(btn.dataset.delete)));
      });
    }

    showToast(msg, type) {
      if (!this.toastEl) return;
      this.toastEl.textContent = msg;
      this.toastEl.style.background = type === 'error' ? '#e62200' : '#18b200';
      this.toastEl.hidden = false;
      clearTimeout(this._timer);
      this._timer = setTimeout(() => this.toastEl.hidden = true, 3000);
    }

    escape(str) {
      const div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    _openSaveNameDialog(defaultName, callback) {
      if (!this.saveNameDialog || !this.saveNameInput) return;

      this.saveNameInput.value = defaultName;
      this.saveNameInput.focus();
      this.saveNameInput.select();

      this.saveNameDialog.classList.add('is-open');
      this.saveNameDialog.setAttribute('aria-hidden', 'false');

      this._saveDialogCallback = callback;
    }

    _closeSaveNameDialog() {
      if (!this.saveNameDialog) return;

      this.saveNameDialog.classList.remove('is-open');
      this.saveNameDialog.setAttribute('aria-hidden', 'true');

      if (this.saveNameInput) {
        this.saveNameInput.value = '';
      }

      this._saveDialogCallback = null;
    }

    _confirmSaveName() {
      if (!this.saveNameInput) return;

      const buildName = this.saveNameInput.value.trim();

      if (!buildName) {
        this.showToast('Please enter a build name', 'error');
        return;
      }

      this._closeSaveNameDialog();

      // Store the build name for _commitSave to use
      this._pendingBuildName = buildName;

      // Trigger the callback if provided
      if (this._saveDialogCallback) {
        this._saveDialogCallback();
      }
    }

    _commitSave(selections, componentIds) {
      const buildName = this._pendingBuildName || 'Untitled Build';

      const saveData = {};
      Object.entries(selections).forEach(([id, data]) => {
        saveData[id] = data.productId;
      });

      const saves = this.getSaves();
      saves.unshift({
        id: Date.now(),
        name: buildName,
        date: new Date().toISOString(),
        data: saveData,
        itemCount: componentIds.length
      });

      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(saves.slice(0, 10)));
      this.updateCount();
      
      // P5: GA4 tracking - build saved
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'build_saved', { build_name: buildName, item_count: componentIds.length });
      } else if (typeof window.dataLayer !== 'undefined' && Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event: 'build_saved', build_name: buildName, item_count: componentIds.length });
      }
      
      this.showToast('Build saved successfully!', 'success');

      this._pendingBuildName = null;
    }
  }

  // Initialize
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => new PCBuilderSaves());
  } else {
    new PCBuilderSaves();
  }
})();
